import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { Plus, Search, Pencil, Trash2, Car as CarIcon } from 'lucide-react';
import api from '../../lib/api';
import type { Vehicle, VehicleListResponse } from '../../types/vehicle';
import { formatPrice } from '../../types/vehicle';
import { VehicleDialog } from './VehicleDialog';
import { usePermissions } from '../../hooks/usePermissions';

export default function Vehicles() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('vehicles:create');
  const canEdit = hasPermission('vehicles:edit');
  const canDelete = hasPermission('vehicles:delete');

  // Fetch vehicles
  const { data, isLoading } = useQuery<VehicleListResponse>({
    queryKey: ['vehicles', page, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');
      if (search) params.append('search', search);

      const response = await api.get(`/vehicles?${params.toString()}`);
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/vehicles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast({
        title: 'Fahrzeug gelöscht',
        description: 'Das Fahrzeug wurde erfolgreich gelöscht.',
      });
    },
    onError: () => {
      toast({
        title: 'Fehler',
        description: 'Das Fahrzeug konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setDialogOpen(true);
  };

  const handleDelete = (vehicle: Vehicle) => {
    if (
      window.confirm(
        `Möchten Sie das Fahrzeug "${vehicle.manufacturer} ${vehicle.model}" wirklich löschen?`
      )
    ) {
      deleteMutation.mutate(vehicle.id);
    }
  };

  const getStatusBadge = (status: Vehicle['status']) => {
    const styles = {
      available: 'bg-green-500/15 text-green-600 dark:text-green-400',
      rented: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
      maintenance: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
      inactive: 'bg-muted text-muted-foreground',
    };

    const labels = {
      available: 'Verfügbar',
      rented: 'Vermietet',
      maintenance: 'Wartung',
      inactive: 'Inaktiv',
    };

    return (
      <span
        className={`px-2 py-1 rounded-pill text-xs font-medium ${
          styles[status] || styles.inactive
        }`}
      >
        {labels[status] || status}
      </span>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <CarIcon className="h-7 w-7" />
              Fahrzeuge
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Mietfahrzeuge
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => {
                setEditingVehicle(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Neues Fahrzeug
            </Button>
          )}
        </div>

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Fahrzeugnummer, Kennzeichen, Hersteller..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Fahrzeuge{data ? ` (${data.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 px-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : !data || data.vehicles.length === 0 ? (
              <div className="text-center py-12 px-4">
                <CarIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Keine Fahrzeuge gefunden</h3>
                <p className="text-muted-foreground mb-4">
                  {search
                    ? 'Keine Fahrzeuge entsprechen Ihrer Suche.'
                    : 'Fügen Sie Ihr erstes Fahrzeug hinzu.'}
                </p>
                {!search && canCreate && (
                  <Button
                    onClick={() => {
                      setEditingVehicle(null);
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Erstes Fahrzeug hinzufügen
                  </Button>
                )}
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Fahrzeug
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Kennzeichen
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Kategorie
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Preis/Tag
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                        KM-Stand
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.vehicles.map((vehicle) => (
                      <tr
                        key={vehicle.id}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium">
                              {vehicle.manufacturer} {vehicle.model}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {vehicle.internal_number}
                              {vehicle.variant && ` • ${vehicle.variant}`}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">
                            {vehicle.license_plate || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm capitalize">
                            {vehicle.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium">
                            {formatPrice(vehicle.price_per_day_cents)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(vehicle.status)}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm">
                            {vehicle.current_mileage.toLocaleString('de-DE')} km
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(vehicle)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(vehicle)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {data.total_pages > 1 && (
                  <div className="px-4 py-3 border-t flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Seite {data.page} von {data.total_pages} • {data.total}{' '}
                      Fahrzeuge gesamt
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                      >
                        Zurück
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === data.total_pages}
                        onClick={() => setPage(page + 1)}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <VehicleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vehicle={editingVehicle}
        onSuccess={() => {
          setDialogOpen(false);
          setEditingVehicle(null);
          queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        }}
      />
    </MainLayout>
  );
}
